import { useQuery } from "@tanstack/react-query";

import { fetchDeploymentRepos } from "@shared/api";
import { getScanStatus } from "@shared/utils";

import { useOrgAssertDefined } from "../useOrgAssertDefined";

interface Options {
  refetchRecentScans?: boolean;
  codeAccess?: boolean;
}

/**
 * @param refetchRecentScans if true, will refetch repos every 5 seconds if any of them is in progress
 * @returns query result with repos from current selected org
 * Repos returned: [
 *    setup_repos (sorted by last completed scan)
 *    not_setup_repos (sorted by name)
 * ]
 */
export const useRepos = ({ refetchRecentScans, codeAccess }: Options = {}) => {
  const org = useOrgAssertDefined();

  return useQuery({
    // NOTE: coupling with `syncRepos` invalidateQueries
    queryKey: ["repos", org.id, codeAccess],
    queryFn: async () => {
      return await fetchDeploymentRepos(org.id, codeAccess).then((response) => {
        return response.repos;
      });
    },
    refetchInterval(data) {
      if (
        refetchRecentScans &&
        data.state.data?.some((repo) =>
          ["starting", "running"].includes(
            getScanStatus(repo, repo.latest_scan, org)
          )
        )
      ) {
        return 5000;
      }
      return false;
    },
  });
};
